import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { convertLatexToMarkup } from "mathlive";
import "mathlive";
// 문제·보기·해설의 수식(convertLatexToMarkup)을 그리는 스타일과 글꼴. 없으면 위첨자·분수가 제자리에 안 선다
import "mathlive/static.css";
import "mathlive/fonts.css";

import apiClient from "../../../services/apiClient";
import secret from "../../../secret.json";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import PhotoCameraIcon from "@mui/icons-material/PhotoCameraOutlined";
import { CoinIcon, PhotoBonusBadge } from "../../reward/RewardIcons";
import { announceBalance, formatPoint } from "../../reward/pointStore";
import "../../reward/Store.css";
import ArrowButton from "../components/widgets/ArrowButton";
import useIsMobile from "../../../hooks/useIsMobile";
import { readSessionFromUrl } from "../../leveltest/session";
import "./MathPage.css";

// 지인에듀 수학 학습지 풀이 화면(2026-10-06). 서버: api/assignment/math_assignment.py
// - 학생은 1/2, sqrt(3), x^2 같은 글자를 치지 않는다. 입력칸은 수학 입력(MathLive)이고, 책에 쓰는 모양 그대로 보인다.
// - 입력칸 모양은 정답 모양으로 서버가 골라 준다(template): 숫자칸 / x = [ ] / ( [ ], [ ] ) / 식.
// - 정답은 제출 뒤에야 온다. 채점은 서버가 수학적으로 같은지 본다.
// - 연산(드릴)과 유형 문항을 색으로 나눈다.
// - 섹션 하나가 세트 하나(30문제)다. 세트마다 제출·채점하고, 채점 뒤에는 '틀린 문제' 탭에서 문제와 해설을 같이 본다.
// - 번호를 줄줄이 늘어놓지 않는다. 가운데 'n / 30' 을 누르면 번호판이 열려 원하는 문제로 뛴다.

const CIRCLED = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧"];
const CATEGORY_LABEL = { computation: "연산", type: "유형" };

// 풀이 사진(서버: api/assignment/math_work_photo.py). 처음 풀 때 세트를 내기 전에 묻는다. 필수면 올려야 넘어가고,
// 선택이면 건너뛸 수 있다. 선생님이 '다시' 라고 하면 채점 뒤 화면에서 다시 올린다
const WorkPhotoModal = ({ assignmentId, identity, isRequired, bonusPoints = 0, onDone, onSkip, onClose }) => {
  const inputRef = useRef(null);
  const [fileList, setFileList] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [message, setMessage] = useState("");
  // 올리고 포인트를 받았으면 잠깐 축하를 보여 주고 넘어간다
  const [awardedPoints, setAwardedPoints] = useState(0);
  const previewList = useMemo(
    () => fileList.map((file) => ({ file, url: file.type.startsWith("image/") ? URL.createObjectURL(file) : "" })),
    [fileList]
  );
  useEffect(() => () => previewList.forEach((one) => one.url && URL.revokeObjectURL(one.url)), [previewList]);

  const send = async () => {
    if (!fileList.length || isSending) return;
    setIsSending(true);
    setMessage("");
    const form = new FormData();
    form.append("assignment_id", assignmentId);
    form.append("student_id", identity.studentId);
    form.append("random_code", identity.randomCode);
    fileList.forEach((file) => form.append("files", file));
    try {
      // apiClient 는 기본이 JSON 이라 파일을 보낼 때는 multipart 를 꼭 적는다(안 적으면 파일이 빠진 채 간다)
      const response = await apiClient.post("/api/v1/assignment/math/work-photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.awarded_points > 0) {
        announceBalance(response.data.balance);
        setAwardedPoints(response.data.awarded_points);
        window.setTimeout(() => onDone(response.data), 1400);
      } else {
        onDone(response.data);
      }
    } catch (error) {
      setMessage(error?.response?.data?.error || "올리지 못했어요. 다시 눌러 주세요.");
      setIsSending(false);
    }
  };

  if (awardedPoints > 0) {
    return (
      <div className="store-sheet">
        <div className="store-sheet__body work-photo-sheet work-photo-sheet--done">
          <PhotoBonusBadge />
          <div className="store-price">
            <CoinIcon size="0.9em" /> +{formatPoint(awardedPoints)}
          </div>
          <div className="store-sheet__title store-invite__title">받았어요!</div>
        </div>
      </div>
    );
  }

  return (
    <div className="store-sheet" onClick={onClose}>
      <div className="store-sheet__body work-photo-sheet" onClick={(event) => event.stopPropagation()}>
        <div className="store-invite__badge">
          <PhotoBonusBadge />
        </div>
        {bonusPoints > 0 ? (
          <>
            <div className="store-sheet__title store-invite__title">
              풀이 사진 올리고 <b>포인트 더 받기</b>
            </div>
            <div className="store-price">
              <CoinIcon size="0.9em" /> +{formatPoint(bonusPoints)}
            </div>
          </>
        ) : (
          <div className="store-sheet__title store-invite__title">풀이 사진 올리기</div>
        )}
        {previewList.length > 0 && (
          <div className="work-photo__previews">
            {previewList.map((one, index) => (
              <div key={`${one.file.name}-${index}`} className="work-photo__preview">
                {one.url ? <img src={one.url} alt="" /> : <span>PDF</span>}
                <button type="button" onClick={() => setFileList((previous) => previous.filter((_, at) => at !== index))}>
                  ✕
                </button>
              </div>
            ))}
            <button type="button" className="work-photo__more" onClick={() => inputRef.current?.click()}>
              +
            </button>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          hidden
          onChange={(event) => {
            setFileList((previous) => [...previous, ...Array.from(event.target.files)].slice(0, 20));
            event.target.value = "";
          }}
        />
        {message && <div className="store-message">{message}</div>}
        {fileList.length === 0 ? (
          <button type="button" className="store-button work-photo-sheet__pick" onClick={() => inputRef.current?.click()}>
            <PhotoCameraIcon /> 푼 종이 찍기
          </button>
        ) : (
          <button type="button" className="store-button" disabled={isSending} onClick={send}>
            {isSending ? "올리는 중" : bonusPoints > 0 ? `올리고 +${formatPoint(bonusPoints)} 받기` : "올리기"}
          </button>
        )}
        {!isRequired && onSkip ? (
          <button type="button" className="store-button store-button--ghost" onClick={onSkip}>
            다음에 할래요
          </button>
        ) : (
          <button type="button" className="store-button store-button--ghost" onClick={onClose}>
            닫기
          </button>
        )}
      </div>
    </div>
  );
};

// 틀린 문제 PDF 두 가지(서버: api/assignment/math_pdf.py). 카카오톡 안 브라우저에서도 열리게 그냥 링크로 연다
const WrongPdfLinks = ({ assignmentId, identity }) => {
  if (!assignmentId || !identity) return null;
  const urlOf = (kind) =>
    `${secret.API_BASE_URL}/api/v1/assignment/math/wrong-pdf?assignment_id=${assignmentId}&kind=${kind}` +
    `&student_id=${identity.studentId}&random_code=${identity.randomCode}`;
  return (
    <div className="math-page__pdf-row">
      <a className="math-page__pdf" href={urlOf("problem")} target="_blank" rel="noopener noreferrer">
        <RefreshIcon className="math-page__pdf-icon" />
        오답 문제지
      </a>
      <a className="math-page__pdf" href={urlOf("explain")} target="_blank" rel="noopener noreferrer">
        <DescriptionIcon className="math-page__pdf-icon" />
        해설지
      </a>
    </div>
  );
};
const COMPUTATION_PAGE_SIZE = 5;
// 한 화면의 답칸들(수식칸·글자칸). Enter 를 누르면 다음 칸으로, 마지막 칸이면 제출
const ANSWER_FIELD_SELECTOR = ".math-page__stage-body math-field:not([read-only]), .math-page__stage-body .math-page__text-field:not([disabled])";
const focusNextAnswerField = (current, onLast) => {
  const fieldList = Array.from(document.querySelectorAll(ANSWER_FIELD_SELECTOR));
  const next = fieldList[fieldList.indexOf(current) + 1];
  if (next) next.focus();
  else if (onLast) onLast();
};

// 수학 사이트 글은 원본 문서의 줄바꿈이 문장 중간에 남아 있다. 표 줄('칸 | 칸')과 빈 줄(문단)만 줄을 바꾸고,
// 나머지 줄바꿈은 띄어쓰기로 잇는다. 그대로 두면 '직각삼각형 / ABC에서' 처럼 문장이 끊긴다
const joinSoftLineBreaks = (text) =>
  String(text || "")
    .split(/\n{2,}/)
    .map((paragraph) =>
      paragraph
        .split("\n")
        .reduce((joined, line, lineIndex, lineList) => {
          if (lineIndex === 0) return line;
          const isTableLine = line.includes("|") || lineList[lineIndex - 1].includes("|");
          return `${joined}${isTableLine ? "\n" : " "}${line}`;
        }, "")
    )
    .join("\n\n");

// '$...$' 를 수식으로, 나머지는 글자로
// 수학 사이트 글에 그림 자리표 〈그림:67〉 가 섞여 오는 문항이 있다(2026-10-06 M-I011105). 그 문항의 image_url_list 를
// 자리표가 나온 순서대로 그 자리에 넣는다. 그림이 아직 안 왔으면 날 자리표 대신 '그림' 표시를 둔다
const FIGURE_PLACEHOLDER_PATTERN = /〈그림:\d+〉/g;
const countFigurePlaceholders = (text) => (String(text || "").match(FIGURE_PLACEHOLDER_PATTERN) || []).length;

const renderMathText = (text, inlineImageList = []) => {
  let figureIndex = 0;
  const part_list = joinSoftLineBreaks(text).split(/(\$[^$]+\$)/g);
  return part_list
    .map((part) => {
      if (part.startsWith("$") && part.endsWith("$") && part.length > 2) {
        try {
          return convertLatexToMarkup(part.slice(1, -1));
        } catch (error) {
          return part;
        }
      }
      return part
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        // 원본 문서의 표 칸 나눔('(가) | 그림', ' | 설명')은 칸 대신 띄어쓰기로
        .replace(/[ \t]*\|[ \t]*/g, " ")
        .replace(FIGURE_PLACEHOLDER_PATTERN, () => {
          const url = inlineImageList[figureIndex++];
          return url
            ? `<img class="math-page__inline-figure" src="${url.replace(/"/g, "&quot;")}" alt="">`
            : '<span class="math-page__figure-missing">그림</span>';
        })
        .replace(/\n/g, "<br>");
    })
    .join("");
};

const MathText = ({ text, className, inlineImageList }) => (
  <span className={className} dangerouslySetInnerHTML={{ __html: renderMathText(text, inlineImageList) }} />
);

// 정답에 섞인 한글(또는, 중근, 평행사변형…)은 \text{} 로 감싸야 수식 글꼴로 붙어 버리지 않는다
const wrapKorean = (latex) =>
  String(latex ?? "")
    .replace(/\\text\{([^}]*)\}/g, "$1")
    .replace(/([가-힣][가-힣\s]*[가-힣]|[가-힣])/g, "\\text{$1}");

const MathLatex = ({ latex }) => (
  <span dangerouslySetInnerHTML={{ __html: latex ? convertLatexToMarkup(wrapKorean(latex)) : "" }} />
);

// MathLive 입력칸 하나. value 는 LaTeX
const MathField = ({ value, onChange, layout, disabled, placeholder, onEnter }) => {
  const fieldRef = useRef(null);
  const onEnterRef = useRef(onEnter);
  onEnterRef.current = onEnter;
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return undefined;
    // 데스크톱에서 Enter 로 제출(2026-10-06 대표)
    const handleKeyDown = (event) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      // 같은 화면에 다음 답칸이 있으면 그리로(다섯 문제 화면, 좌표·근이 여러 칸인 답). 없으면 제출
      const fieldList = Array.from(document.querySelectorAll(ANSWER_FIELD_SELECTOR));
      const next = fieldList[fieldList.indexOf(field) + 1];
      if (next) next.focus();
      else if (onEnterRef.current) onEnterRef.current();
    };
    field.addEventListener("keydown", handleKeyDown);
    return () => field.removeEventListener("keydown", handleKeyDown);
  }, []);
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return undefined;
    field.mathVirtualKeyboardPolicy = "auto";
    if (field.value !== (value || "")) field.value = value || "";
    const handleInput = () => onChange(field.value);
    field.addEventListener("input", handleInput);
    return () => field.removeEventListener("input", handleInput);
  }, [value, onChange]);
  useEffect(() => {
    if (window.mathVirtualKeyboard) {
      window.mathVirtualKeyboard.layouts = layout === "number" ? ["numeric"] : ["numeric", "symbols", "alphabetic"];
    }
  }, [layout]);
  return (
    <math-field
      ref={fieldRef}
      class={`math-page__field${layout === "number" ? " math-page__field--short" : ""}${disabled ? " math-page__field--disabled" : ""}`}
      read-only={disabled ? "" : undefined}
      placeholder={placeholder || ""}
      onFocus={() => {
        if (window.mathVirtualKeyboard) {
          window.mathVirtualKeyboard.layouts = layout === "number" ? ["numeric"] : ["numeric", "symbols", "alphabetic"];
        }
      }}
    />
  );
};

const splitCoordinate = (value) => {
  const inner = String(value || "").replace(/^\\left\(|^\(/, "").replace(/\\right\)$|\)$/, "");
  const commaIndex = inner.indexOf(",");
  return commaIndex < 0 ? [inner, ""] : [inner.slice(0, commaIndex), inner.slice(commaIndex + 1)];
};

// 한글 낱말 답(정답이 '평행사변형' 같은 것)이나 (가)(나) 칸을 받는 글자칸. 수식칸은 한글을 못 친다
const TextField = ({ value, onChange, disabled, onEnter }) => (
  <input
    type="text"
    className="math-page__text-field"
    value={value}
    disabled={disabled}
    onChange={(event) => onChange(event.target.value)}
    onKeyDown={(event) => {
      if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
      event.preventDefault();
      focusNextAnswerField(event.currentTarget, onEnter);
    }}
  />
);

const readPartMap = (value) => {
  try {
    return value ? JSON.parse(value) : {};
  } catch (error) {
    return {};
  }
};

const ShortAnswer = ({ problem, value, onChange, disabled, onEnter }) => {
  const template = problem.template || { kind: "expression" };
  const unit = template.unit ? <span className="math-page__unit">{template.unit}</span> : null;
  if (template.kind === "text") {
    return (
      <div className="math-page__answer-row">
        <TextField value={value} onChange={onChange} disabled={disabled} onEnter={onEnter} />
      </div>
    );
  }
  if (template.kind === "parts") {
    // 칸별 답을 {"가": "…"} 로 모은다. 다 비면 빈 답
    const partMap = readPartMap(value);
    const update = (label, text) => {
      const next = { ...partMap, [label]: text };
      onChange(Object.values(next).some((one) => String(one).trim()) ? JSON.stringify(next) : "");
    };
    return (
      <div className="math-page__parts">
        {template.label_list.map((label) => (
          <label key={label} className="math-page__part">
            <span className="math-page__part-label">({label})</span>
            <TextField value={partMap[label] || ""} onChange={(text) => update(label, text)} disabled={disabled} onEnter={onEnter} />
          </label>
        ))}
      </div>
    );
  }
  if (template.kind === "roots") {
    // x = [ ] 또는 x = [ ]. 값만 쉼표로 이어 보낸다(서버가 순서와 상관없이 짝짓는다)
    const rootList = String(value || "").split(",");
    const update = (rootIndex, next) => {
      const nextList = Array.from({ length: template.count }, (_, index) => (index === rootIndex ? next : rootList[index] || ""));
      onChange(nextList.some((one) => one.trim()) ? nextList.join(",") : "");
    };
    return (
      <div className="math-page__answer-row math-page__answer-row--roots">
        {Array.from({ length: template.count }, (_, rootIndex) => (
          <span key={rootIndex} className="math-page__root">
            {rootIndex > 0 && <span className="math-page__or">또는</span>}
            <MathLatex latex={`${template.prefix}=`} />
            <MathField onEnter={onEnter} value={rootList[rootIndex] || ""} onChange={(next) => update(rootIndex, next)} layout="number" disabled={disabled} />
          </span>
        ))}
      </div>
    );
  }
  if (template.kind === "assignment") {
    return (
      <div className="math-page__answer-row">
        <MathLatex latex={`${template.prefix}=`} />
        <MathField onEnter={onEnter} value={value} onChange={onChange} layout="number" disabled={disabled} />
        {unit}
      </div>
    );
  }
  if (template.kind === "coordinate") {
    const [left, right] = splitCoordinate(value);
    const update = (nextLeft, nextRight) => onChange(`(${nextLeft},${nextRight})`);
    return (
      <div className="math-page__answer-row">
        <span className="math-page__paren">(</span>
        <MathField onEnter={onEnter} value={left} onChange={(next) => update(next, right)} layout="number" disabled={disabled} />
        <span className="math-page__paren">,</span>
        <MathField onEnter={onEnter} value={right} onChange={(next) => update(left, next)} layout="number" disabled={disabled} />
        <span className="math-page__paren">)</span>
      </div>
    );
  }
  return (
    <div className="math-page__answer-row">
      <MathField onEnter={onEnter}
        value={value}
        onChange={onChange}
        layout={template.kind === "number" ? "number" : "expression"}
        disabled={disabled}
      />
      {unit}
    </div>
  );
};

const isAnswered = (answer) =>
  answer !== undefined && answer !== null && answer !== "" && !(Array.isArray(answer) && answer.length === 0);

const answerTextOf = (answerKey) =>
  Array.isArray(answerKey?.answer) ? answerKey.answer.join(", ") : String(answerKey?.answer ?? "");

// 문제 하나(태그·문제·입력·해설). 풀이 화면과 틀린 문제 목록이 같이 쓴다
const ProblemCard = ({ problem, number, value, onChange, isFinished, graded, answerKey, footer = null, onEnter, onPick, isCompact = false }) => {
  // 그림 자리표가 있고 그림이 그만큼 왔으면 글 속 제자리에 넣는다. 나머지 그림은 늘 하던 대로 글 아래
  const placeholderCount = countFigurePlaceholders(problem.question);
  const inlineImageList =
    placeholderCount && (problem.image_url_list || []).length >= placeholderCount
      ? problem.image_url_list.slice(0, placeholderCount)
      : [];
  return (
  <div
    className={`math-page__card${isCompact ? " math-page__card--compact" : ""}${
      isFinished && graded?.is_correct === true ? " math-page__card--right" : ""
    }${isFinished && graded?.is_correct === false ? " math-page__card--wrong" : ""}`}
  >
    <div className="math-page__tags">
      <span className={`math-page__category math-page__category--${problem.category}`}>
        {CATEGORY_LABEL[problem.category] || "문제"}
      </span>
      {problem.type_label && <span className="math-page__tag">{problem.type_label}</span>}
      {isFinished && graded?.is_correct === true && <span className="math-page__result math-page__result--right">정답</span>}
      {isFinished && graded?.is_correct === false && <span className="math-page__result math-page__result--wrong">오답</span>}
    </div>
    <div className="math-page__question">
      <span className="math-page__no">{number}.</span>
      <MathText text={problem.question} inlineImageList={inlineImageList} />
    </div>
    {(problem.image_url_list || []).slice(inlineImageList.length).map((url) => (
      <img key={url} className="math-page__image" src={url} alt="" />
    ))}

    {problem.answer_type === "choice" && (
      <div className="math-page__choices">
        {problem.choice_list.map((choice, choiceIndex) => {
          const choiceNumber = String(choiceIndex + 1);
          const isPicked = String(value) === choiceNumber;
          const isAnswer =
            isFinished &&
            (Array.isArray(answerKey?.answer) ? answerKey.answer.map(String) : [String(answerKey?.answer)]).includes(choiceNumber);
          return (
            <button
              key={choiceNumber}
              type="button"
              disabled={isFinished}
              className={`math-page__choice${isPicked ? " math-page__choice--picked" : ""}${
                isAnswer ? " math-page__choice--answer" : ""
              }${isFinished && isPicked && !isAnswer ? " math-page__choice--wrong" : ""}`}
              onClick={() => {
                onChange(choiceNumber);
                if (onPick) onPick(choiceNumber);
              }}
            >
              <span className="math-page__circled">{CIRCLED[choiceIndex]}</span>
              <MathText text={choice} />
            </button>
          );
        })}
      </div>
    )}
    {problem.answer_type === "short" && (
      <ShortAnswer problem={problem} value={value} onChange={onChange} disabled={isFinished} onEnter={onEnter} />
    )}
    {problem.answer_type === "essay" && (
      <textarea
        className="math-page__essay"
        value={value}
        disabled={isFinished}
        onChange={(event) => onChange(event.target.value)}
      />
    )}

    {isFinished && answerKey && (
      <div className="math-page__explain">
        {problem.answer_type !== "choice" && answerKey.answer !== undefined && (
          <div className="math-page__explain-answer">
            정답 <MathLatex latex={answerTextOf(answerKey)} />
          </div>
        )}
        {answerKey.explanation && <MathText text={answerKey.explanation} className="math-page__explain-text" />}
        {answerKey.explanation_image_url && <img className="math-page__image" src={answerKey.explanation_image_url} alt="" />}
      </div>
    )}
    {footer}
  </div>
  );
};

const MathSetPage = ({ setCurrentStep, isPreview = false }) => {
  const { section } = useSelector((state) => state.assignment);
  // 풀이 사진 창: null 이면 닫힘. { afterUpload } 는 올리거나 건너뛴 뒤 할 일(채점)
  const [photoAsk, setPhotoAsk] = useState(null);
  // 이번 화면에서 이미 올렸거나 건너뛰었는가. 상태로 두면 바로 이어 부르는 채점이 옛 값을 봐서 창이 또 뜬다
  const photoAnsweredRef = useRef(false);
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [valueById, setValueById] = useState({});
  const [index, setIndex] = useState(0);
  const [tab, setTab] = useState("all");
  const [isJumpOpen, setIsJumpOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const identity = useMemo(() => readSessionFromUrl(), []);

  const applyData = useCallback((next) => {
    setData(next);
    const saved = {};
    (next.response_list || []).forEach((one) => {
      saved[one.problem_id] = one.value;
    });
    setValueById(saved);
    const hasWrong = (next.response_list || []).some((one) => one.is_correct === false);
    setTab(next.is_finished && hasWrong ? "wrong" : "all");
  }, []);

  useEffect(() => {
    if (!section?.id || !identity) return;
    apiClient
      .get("/api/v1/assignment/math/section", {
        params: { section_id: section.id, student_id: identity.studentId, random_code: identity.randomCode },
      })
      .then((response) => applyData(response.data))
      .catch(() => setError("문제를 불러오지 못했어요."));
  }, [section?.id, identity, applyData]);

  const setValue = useCallback(
    (problemId) => (next) => setValueById((previous) => ({ ...previous, [problemId]: next })),
    []
  );

  const submit = async (isFinal, valueOverride = {}) => {
    if (!identity || !data || data.is_finished || isPreview) return;
    setIsSaving(true);
    try {
      const response = await apiClient.post("/api/v1/assignment/math/section/submit", {
        section_id: data.section_id,
        student_id: identity.studentId,
        random_code: identity.randomCode,
        response_list: data.problem_list.map((problem) => ({
          problem_id: problem.problem_id,
          value: { ...valueById, ...valueOverride }[problem.problem_id] ?? "",
        })),
        is_final: isFinal,
      });
      if (isFinal) {
        applyData(response.data);
        setIndex(0);
        window.scrollTo(0, 0);
      }
    } catch (caught) {
      // 필수 풀이 사진을 안 올렸으면 서버가 막는다. 오류 대신 사진 창을 띄우고, 올리면 다시 낸다
      if (caught?.response?.data?.code === "work_photo_required") {
        photoAnsweredRef.current = false;
        setPhotoAsk({ afterUpload: () => submit(isFinal, valueOverride) });
        return;
      }
      setError("저장하지 못했어요. 다시 눌러 주세요.");
    } finally {
      setIsSaving(false);
    }
  };

  if (error) return <div className="math-page__message">{error}</div>;
  if (!data) return null;

  const problemList = data.problem_list;
  if (!problemList[index]) return <div className="math-page__message">문항이 없어요.</div>;
  const isFinished = data.is_finished;
  const keyById = Object.fromEntries((data.answer_key || []).map((one) => [one.problem_id, one]));
  const gradedById = Object.fromEntries((data.response_list || []).map((one) => [one.problem_id, one]));
  const wrongIndexList = problemList
    .map((one, problemIndex) => (gradedById[one.problem_id]?.is_correct === false ? problemIndex : -1))
    .filter((problemIndex) => problemIndex >= 0);
  // 연산 문제만 있는 세트는 한 화면에 5문제씩(2026-10-06 대표: 몇백 개를 하나씩 넘기면 못 푼다). 다섯 개 풀고 제출하면
  // 다음 다섯 개로. 그림이 있는 유형 문제는 그대로 한 문제씩
  const pageSize = problemList.every((one) => one.category === "computation") ? COMPUTATION_PAGE_SIZE : 1;
  const pageStart = Math.floor(index / pageSize) * pageSize;
  const pageProblemList = problemList.slice(pageStart, pageStart + pageSize);
  const isLast = pageStart + pageSize >= problemList.length;

  const moveTo = (nextIndex, valueOverride = {}) => {
    if (nextIndex < 0 || nextIndex >= problemList.length) return;
    if (!isFinished) submit(false, valueOverride);
    setIndex(nextIndex);
    setIsJumpOpen(false);
  };
  // 세트 채점. 안 푼 문제가 있으면 묻고, 그만두면 처음 안 푼 문제로 데려간다. 풀이 사진을 받는 과제면 그 전에 묻는다
  const finishSet = (valueOverride = {}) => {
    if (data.work_photo?.needed && !isPreview && !photoAnsweredRef.current) {
      setPhotoAsk({ afterUpload: () => finishSet(valueOverride) });
      return;
    }
    const merged = { ...valueById, ...valueOverride };
    const firstEmptyIndex = problemList.findIndex((one) => !isAnswered(merged[one.problem_id]));
    if (firstEmptyIndex >= 0) {
      const leftCount = problemList.filter((one) => !isAnswered(merged[one.problem_id])).length;
      if (!window.confirm(`${leftCount}문제를 안 풀었어요. 그래도 채점할까요?`)) {
        moveTo(firstEmptyIndex);
        return;
      }
    }
    submit(true, valueOverride);
  };
  // 이 화면(문제 하나 또는 다섯)을 내면 다음 화면으로, 마지막이면 세트 채점
  const submitProblem = (valueOverride = {}) => {
    if (isFinished || isSaving) return;
    if (isLast) finishSet(valueOverride);
    else moveTo(pageStart + pageSize, valueOverride);
  };

  const dotStateOf = (one) => {
    if (isFinished) {
      const judged = gradedById[one.problem_id]?.is_correct;
      return judged === true ? "right" : judged === false ? "wrong" : "none";
    }
    return isAnswered(valueById[one.problem_id]) ? "done" : "none";
  };

  const photoModal = photoAsk && (
    <WorkPhotoModal
      assignmentId={data.assignment_id}
      identity={identity}
      isRequired={data.work_photo?.policy === "required" || data.work_photo?.rating === "retry"}
      bonusPoints={data.work_photo?.bonus_points || 0}
      onClose={() => setPhotoAsk(null)}
      onSkip={() => {
        photoAnsweredRef.current = true;
        setPhotoAsk(null);
        photoAsk.afterUpload?.();
      }}
      onDone={(workPhoto) => {
        photoAnsweredRef.current = true;
        setData((previous) => ({ ...previous, work_photo: workPhoto }));
        setPhotoAsk(null);
        photoAsk.afterUpload?.();
      }}
    />
  );

  return (
    <div className="math-page">
      {photoModal}
      {isFinished && data.work_photo?.rating === "retry" && (
        <div className="math-page__photo-retry">
          선생님이 풀이 사진을 다시 올려 달래요
          <button type="button" className="math-page__submit" onClick={() => setPhotoAsk({ afterUpload: null })}>
            다시 올리기
          </button>
        </div>
      )}
      <div className="math-page__top">
        <div className="math-page__title">
          {data.title}
          {data.type_name && <span className="math-page__set">{data.type_name}</span>}
        </div>
        {isFinished && (
          <div className="math-page__score">
            {data.set_judged_count ? `${data.set_right_count} / ${data.set_judged_count} 정답` : "채점 완료"}
          </div>
        )}
      </div>

      {isFinished && (
        <div className="math-page__tabs" role="tablist">
          <button type="button" aria-selected={tab === "all"} onClick={() => setTab("all")}>
            전체 문제
          </button>
          <button type="button" aria-selected={tab === "wrong"} onClick={() => setTab("wrong")}>
            틀린 문제 {wrongIndexList.length}
          </button>
        </div>
      )}

      {isFinished && tab === "wrong" ? (
        <div className="math-page__wrong-list">
          {wrongIndexList.length > 0 && <WrongPdfLinks assignmentId={data.assignment_id} identity={identity} />}
          {wrongIndexList.length === 0 && <div className="math-page__message">틀린 문제가 없어요.</div>}
          {wrongIndexList.map((problemIndex) => {
            const one = problemList[problemIndex];
            return (
              <ProblemCard
                key={one.problem_id}
                problem={one}
                number={one.no || problemIndex + 1}
                value={valueById[one.problem_id] ?? ""}
                onChange={() => {}}
                isFinished
                graded={gradedById[one.problem_id]}
                answerKey={keyById[one.problem_id]}
              />
            );
          })}
        </div>
      ) : (
        <div className="math-page__stage">
          {!isMobile && (
            <ArrowButton direction="left" onClick={() => moveTo(pageStart - pageSize)} visibleCondition={pageStart > 0} />
          )}
          <div className="math-page__stage-body">
            {pageProblemList.map((problem, offset) => (
              <ProblemCard
                key={problem.problem_id}
                problem={problem}
                // 세트 2 부터도 학습지 원래 번호(31, 32…)로 보인다
                number={problem.no || pageStart + offset + 1}
                // 연산 다섯 문제 화면은 문제와 답칸을 한 줄에
                isCompact={pageSize > 1}
                value={valueById[problem.problem_id] ?? ""}
                onChange={setValue(problem.problem_id)}
                isFinished={isFinished}
                graded={gradedById[problem.problem_id]}
                answerKey={keyById[problem.problem_id]}
                // 답칸에서 Enter: 이 화면의 다음 칸으로, 마지막 칸이면 제출(MathField 가 다음 칸을 찾는다)
                onEnter={() => submitProblem()}
                // 한 문제씩 보는 객관식은 고르는 것이 곧 제출이다. 잠깐 보여 주고 넘어간다
                onPick={
                  pageSize === 1
                    ? (choiceNumber) =>
                        window.setTimeout(() => submitProblem({ [problem.problem_id]: choiceNumber }), 250)
                    : undefined
                }
                footer={
                  !isFinished &&
                  pageSize === 1 &&
                  problem.answer_type !== "choice" && (
                    // 주관식만(다섯 문제 화면은 맨 아래 하나). 답칸 바로 아래 오른쪽
                    <div className="math-page__submit-row">
                      <button
                        type="button"
                        className="math-page__submit"
                        disabled={isSaving || isPreview}
                        onClick={() => submitProblem()}
                      >
                        {isSaving ? "채점 중" : "제출하기"}
                      </button>
                    </div>
                  )
                }
              />
            ))}
            {/* 다섯 문제 화면은 마지막 카드 안이 아니라 카드들 아래에 */}
            {!isFinished && pageSize > 1 && (
              <div className="math-page__submit-row math-page__submit-row--page">
                <button type="button" className="math-page__submit" disabled={isSaving || isPreview} onClick={() => submitProblem()}>
                  {isSaving ? "채점 중" : "제출하기"}
                </button>
              </div>
            )}
          </div>
          {!isMobile && <ArrowButton direction="right" onClick={() => moveTo(pageStart + pageSize)} visibleCondition={!isLast} />}
        </div>
      )}

      {!(isFinished && tab === "wrong") && (
        <div className="math-page__bar">
          {isMobile && <ArrowButton direction="left" onClick={() => moveTo(pageStart - pageSize)} visibleCondition={pageStart > 0} />}
          <div className="math-page__jump">
            <button type="button" className="math-page__counter" onClick={() => setIsJumpOpen(!isJumpOpen)}>
              {pageSize > 1
                ? `${pageStart + 1}-${pageStart + pageProblemList.length}`
                : index + 1}{" "}
              / {problemList.length} <span className="math-page__caret">{isJumpOpen ? "▴" : "▾"}</span>
            </button>
            {isJumpOpen && <div className="math-page__jump-backdrop" onClick={() => setIsJumpOpen(false)} />}
            {isJumpOpen && (
              <div className="math-page__jump-grid">
                <div className="math-page__jump-head">
                  문제 이동
                  <button type="button" onClick={() => setIsJumpOpen(false)}>
                    ✕
                  </button>
                </div>
                {problemList.map((one, dotIndex) => (
                  <button
                    key={one.problem_id}
                    type="button"
                    className={`math-page__dot math-page__dot--${dotStateOf(one)}${dotIndex >= pageStart && dotIndex < pageStart + pageSize ? " math-page__dot--current" : ""}`}
                    onClick={() => moveTo(dotIndex)}
                  >
                    {one.no || dotIndex + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
          {isMobile && <ArrowButton direction="right" onClick={() => moveTo(pageStart + pageSize)} visibleCondition={!isLast} />}
        </div>
      )}

      {isFinished && (
        <div className="math-page__leave">
          <button type="button" className="math-page__submit" onClick={() => setCurrentStep(1)}>
            나가기
          </button>
        </div>
      )}
    </div>
  );
};

// 빠른 정답 입력(2026-10-06): 종이에 푼 아이가 학습지 전체(모든 세트)의 답만 넣는다. 다 채점하면 같은 자리가 '틀린 문제'.
// 서버: api/assignment/math_assignment.py quick_payload · submit_quick
const MathQuickPage = ({ paragraphId, setCurrentStep, isPreview }) => {
  const identity = useMemo(() => readSessionFromUrl(), []);
  const [photoAsk, setPhotoAsk] = useState(null);
  const photoAnsweredRef = useRef(false);
  const [data, setData] = useState(null);
  const [valueById, setValueById] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  const applyData = useCallback((next) => {
    setData(next);
    setValueById(next.value_by_id || {});
  }, []);

  useEffect(() => {
    if (!identity || !paragraphId) return;
    apiClient
      .get("/api/v1/assignment/math/quick", {
        params: { paragraph_id: paragraphId, student_id: identity.studentId, random_code: identity.randomCode },
      })
      .then((response) => applyData(response.data))
      .catch(() => setError("불러오지 못했어요."));
  }, [identity, paragraphId, applyData]);

  const setValue = useCallback(
    (problemId) => (next) => setValueById((previous) => ({ ...previous, [problemId]: next })),
    []
  );

  const send = async (isFinal) => {
    if (!identity || !data || isPreview) return;
    setIsSaving(true);
    try {
      const response = await apiClient.post("/api/v1/assignment/math/quick", {
        paragraph_id: paragraphId,
        student_id: identity.studentId,
        random_code: identity.randomCode,
        response_list: data.problem_list
          .filter((one) => !one.is_finished)
          .map((one) => ({ problem_id: one.problem_id, value: valueById[one.problem_id] ?? "" })),
        is_final: isFinal,
      });
      applyData(response.data);
      if (isFinal) window.scrollTo(0, 0);
    } catch (caught) {
      if (caught?.response?.data?.code === "work_photo_required") {
        photoAnsweredRef.current = false;
        setPhotoAsk({ afterUpload: () => send(isFinal) });
        return;
      }
      setError("저장하지 못했어요. 다시 눌러 주세요.");
    } finally {
      setIsSaving(false);
    }
  };

  if (error) return <div className="math-page__message">{error}</div>;
  if (!data) return null;

  if (data.is_all_finished) {
    return (
      <div className="math-page">
        <div className="math-page__top">
          <div className="math-page__title">
            {data.title}
            <span className="math-page__set">틀린 문제 {data.wrong_list.length}</span>
          </div>
          <div className="math-page__score">
            {data.judged_count ? `${data.right_count} / ${data.judged_count} 정답` : "채점 완료"}
          </div>
        </div>
        <div className="math-page__wrong-list">
          {data.wrong_list.length > 0 && <WrongPdfLinks assignmentId={data.assignment_id} identity={identity} />}
          {data.wrong_list.length === 0 && <div className="math-page__message">틀린 문제가 없어요.</div>}
          {data.wrong_list.map((one) => (
            <ProblemCard
              key={one.problem_id}
              problem={one}
              number={one.no}
              value={one.value ?? ""}
              onChange={() => {}}
              isFinished
              graded={one}
              answerKey={one.answer_key}
            />
          ))}
        </div>
        <div className="math-page__leave">
          <button type="button" className="math-page__submit" onClick={() => setCurrentStep(1)}>
            나가기
          </button>
        </div>
      </div>
    );
  }

  const openProblemList = data.problem_list.filter((one) => !one.is_finished);
  const leftCount = openProblemList.filter((one) => !isAnswered(valueById[one.problem_id])).length;
  const finish = () => {
    if (data.work_photo?.needed && !isPreview && !photoAnsweredRef.current) {
      setPhotoAsk({ afterUpload: finish });
      return;
    }
    if (!window.confirm(leftCount > 0 ? `${leftCount}문제를 안 적었어요. 그래도 채점할까요?` : "채점할까요?")) return;
    send(true);
  };
  const quickPhotoModal = photoAsk && (
    <WorkPhotoModal
      assignmentId={data.assignment_id}
      identity={identity}
      isRequired={data.work_photo?.policy === "required" || data.work_photo?.rating === "retry"}
      bonusPoints={data.work_photo?.bonus_points || 0}
      onClose={() => setPhotoAsk(null)}
      onSkip={() => {
        photoAnsweredRef.current = true;
        setPhotoAsk(null);
        photoAsk.afterUpload?.();
      }}
      onDone={(workPhoto) => {
        photoAnsweredRef.current = true;
        setData((previous) => ({ ...previous, work_photo: workPhoto }));
        setPhotoAsk(null);
        photoAsk.afterUpload?.();
      }}
    />
  );

  return (
    <div className="math-page math-page--quick">
      {quickPhotoModal}
      <div className="math-page__top">
        <div className="math-page__title">
          {data.title}
          <span className="math-page__set">빠른 정답 입력</span>
        </div>
      </div>
      <div className="math-quick__grid math-page__stage-body">
        {openProblemList.map((one) => (
          <div key={one.problem_id} className="math-quick__cell">
            <div className="math-quick__no">{one.no}번</div>
            {one.answer_type === "choice" ? (
              <div className="math-quick__choices">
                {Array.from({ length: one.choice_count || 5 }, (_, choiceIndex) => {
                  const choiceNumber = String(choiceIndex + 1);
                  return (
                    <button
                      key={choiceNumber}
                      type="button"
                      className={`math-quick__choice${String(valueById[one.problem_id]) === choiceNumber ? " math-quick__choice--picked" : ""}`}
                      onClick={() => setValue(one.problem_id)(choiceNumber)}
                    >
                      {CIRCLED[choiceIndex]}
                    </button>
                  );
                })}
              </div>
            ) : (
              <ShortAnswer
                problem={one}
                value={valueById[one.problem_id] ?? ""}
                onChange={setValue(one.problem_id)}
                disabled={false}
                onEnter={finish}
              />
            )}
          </div>
        ))}
      </div>
      <div className="math-quick__actions">
        <button type="button" className="math-page__submit math-page__submit--ghost" disabled={isSaving || isPreview} onClick={() => send(false)}>
          저장
        </button>
        <button type="button" className="math-page__submit" disabled={isSaving || isPreview} onClick={finish}>
          {isSaving ? "채점 중" : "채점하기"}
        </button>
      </div>
    </div>
  );
};

const MathPage = (props) => {
  const { section } = useSelector((state) => state.assignment);
  if (section?.quickParagraphId) {
    return <MathQuickPage paragraphId={section.quickParagraphId} setCurrentStep={props.setCurrentStep} isPreview={props.isPreview} />;
  }
  return <MathSetPage {...props} />;
};

export default MathPage;
